// Razor/Cut tool removed — splitting is via right-click Split at Playhead
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PanToolIcon from '@mui/icons-material/PanTool';
import RedoIcon from '@mui/icons-material/Redo';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Box, Button, Chip, IconButton, ListItemIcon, Menu, MenuItem, Slider, Switch, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { autoSplitCues } from '../dubsync/dubSyncUtils';
import { useDubSync } from '../dubsync/useDubSync';
import { useEditor } from '../EditorContext';
import { Tools } from '../editorReducer';
import {
    getOutputDuration,
    secondsToTimecode
} from '../editorUtils';
import DubSyncTrackBar from './DubSyncTrackBar';
import LogoTrackBar from './LogoTrackBar';
import MusicTrackBar from './MusicTrackBar';
import { useTimelineScrub } from './Playhead';
import SubtitleTrackBar from './SubtitleTrackBar';
import TimeRuler from './TimeRuler';
import VideoTrack from './VideoTrack';

/* ── Styled components ──────────────────────────────────────────── */

const PanelRoot = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #0d0d17;
    overflow: hidden;
    user-select: none;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: #161625;
    border-bottom: 1px solid #2a2a3a;
    flex-shrink: 0;
    flex-wrap: wrap;
`;

const ToolBtn = styled(IconButton)`
    && {
        color: ${({ active }) => (active === 'true' ? '#64b5f6' : '#888')};
        background: ${({ active }) => (active === 'true' ? 'rgba(100,181,246,0.15)' : 'transparent')};
        border-radius: 4px;
        padding: 4px;
        &:hover {
            background: rgba(255, 255, 255, 0.08);
        }
    }
`;

const Divider = styled.div`
    width: 1px;
    height: 22px;
    background: #333;
    margin: 0 4px;
    flex-shrink: 0;
`;

const RulerRow = styled.div`
    height: 32px;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
`;

const TracksArea = styled.div`
    flex: 1;
    overflow-x: auto;
    overflow-y: auto;
    position: relative;

    /* Custom scrollbar */
    ::-webkit-scrollbar { height: 6px; width: 6px; }
    ::-webkit-scrollbar-track { background: #1a1a2e; }
    ::-webkit-scrollbar-thumb { background: #444; border-radius: 3px; }
`;

const TracksContent = styled.div`
    position: relative;
    min-height: 100%;
`;

const TrackRow = styled.div`
    display: flex;
    align-items: stretch;
`;

const TrackLabel = styled.div`
    width: 90px;
    min-width: 90px;
    display: flex;
    align-items: center;
    padding: 0 8px;
    font-size: 12px;
    color: #888;
    border-bottom: 1px solid #2a2a3a;
    background: #161625;
    flex-shrink: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    /* Freeze while the track content scrolls horizontally */
    position: sticky;
    left: 0;
    z-index: 5;
`;

const TrackContent = styled.div`
    flex: 1;
    position: relative;
    min-width: 0;
`;

const DeleteHint = styled.div`
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(229, 57, 53, 0.9);
    color: #fff;
    font-size: 13px;
    padding: 4px 12px;
    border-radius: 4px;
    pointer-events: none;
    z-index: 30;
    white-space: nowrap;
`;

const ApplyToRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 8px;
    background: #12121e;
    border-bottom: 1px solid #2a2a3a;
    flex-shrink: 0;
    flex-wrap: wrap;
    min-height: 28px;
`;

const ContextHintBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    background: #0e1a2a;
    border-bottom: 1px solid #1e3a5f;
    flex-shrink: 0;
    min-height: 24px;
    font-size: 13px;
    color: #64b5f6;
`;

const HintStep = styled.span`
    background: rgba(100, 181, 246, 0.12);
    border: 1px solid rgba(100, 181, 246, 0.3);
    border-radius: 3px;
    padding: 1px 5px;
    font-size: 12px;
    color: #90caf9;
    white-space: nowrap;
`;

const TOOL_HINTS = {
    select: [
        { step: '1', text: 'Click a segment to select it (turns orange)' },
        { step: '2', text: 'Press ⌥ Delete  or click 🗑 in the toolbar to remove it' },
    ],
    hand: [
        { step: '', text: 'Click and drag to scroll the timeline' },
    ],
};

/* ── Main Component ─────────────────────────────────────────────── */

export default function TimelinePanel() {
    const {
        state,
        setTool,
        setZoom,
        addCutMarkerWithSplit,
        removeCutMarker,
        selectSegment,
        rippleDeleteSegment,
        setPlayhead,
        undo,
        redo,
        toggleMuteVideo,
        setVideoVolume,
        updateAudioTrack,
        updateLogoTrack,
        removeDubSyncTrack: removeDubSyncTrackFn,
        hiddenTimelineLangs,
        updateDubSyncCues,
        setDubSyncCues,
        setPendingDubSyncEditsRef,
        moveSubtitleCues,
    } = useEditor();

    const {
        duration,
        cutMarkers,
        cuts,
        subtitlesByLanguage,
        activeTool,
        zoomLevel,
        selectedSegment,
        playheadTime,
        videoTitle,
        audioTracks,
        logoTracks,
        dubSyncTracks,
        muteVideoAudio,
        videoStartOffset,
        videoEndOffset,
        videoId,
    } = state;

    const { syncMagic, renderAudio: renderDubAudio, saveTimeline: saveDubTimeline, bootstrapManual } = useDubSync(videoId, videoTitle);

    // ── DubSync inline edit state ──────────────────────────────────────────────
    // Instead of a modal dialog, editing happens directly in the timeline row.
    const [dubSyncEditLang, setDubSyncEditLang] = useState(null);
    const [selectedDubSyncCueId, setSelectedDubSyncCueId] = useState(null);
    // pendingCuesMap: local edits not yet saved — keyed by language
    const [pendingCuesMap, setPendingCuesMap] = useState({});
    // undo history for DubSync cue edits (local stack, separate from the reducer)
    const [pendingCuesHistory, setPendingCuesHistory] = useState([]);
    // cursor position (selectionStart) in the cue text editor — drives the split preview
    const [splitCursorPos, setSplitCursorPos] = useState(null);

    // ── DubSync testing flags (shown in the track context menu) ───────────────
    const [dubSyncForceTranscribe, setDubSyncForceTranscribe] = useState(false);
    const [dubSyncNoCheapMode,     setDubSyncNoCheapMode]     = useState(false);
    const [dubSyncWithSrtHint,     setDubSyncWithSrtHint]     = useState(false);
    // Per-language mode: 'ai' (default) or 'manual'
    const [dubSyncModeMap,         setDubSyncModeMap]         = useState({});
    // Per-language: whether AI advanced options panel is expanded
    const [showAiOptionsMap,       setShowAiOptionsMap]       = useState({});

    const savePendingCueSnapshot = () =>
        setPendingCuesHistory((h) => [...h.slice(-30), pendingCuesMap]);

    // Split a cue in edit mode at the given time, distributing text proportionally
    const splitPendingCue = (lang, cueId, splitAtTime) => {
        savePendingCueSnapshot();
        setPendingCuesMap((prev) => {
            const cues = prev[lang] || subtitlesByLanguage[`${lang}-DubSync`] || [];
            const idx = cues.findIndex((c) => c.id === cueId);
            if (idx === -1) return prev;
            const cue = cues[idx];
            // Split text at nearest word boundary to the midpoint
            const words = (cue.text || '').split(' ').filter(Boolean);
            const mid = Math.max(1, Math.ceil(words.length / 2));
            const textA = words.slice(0, mid).join(' ') || cue.text;
            const textB = words.slice(mid).join(' ')  || cue.text;
            const cueA = { ...cue,                              endTime: splitAtTime, text: textA };
            const cueB = { id: `split-${Date.now()}`, startTime: splitAtTime, endTime: cue.endTime, text: textB };
            return { ...prev, [lang]: [...cues.slice(0, idx), cueA, cueB, ...cues.slice(idx + 1)] };
        });
    };

    // Clear split cursor preview whenever the selected cue changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setSplitCursorPos(null); }, [selectedDubSyncCueId]);

    // Keep the context ref up-to-date so handleSave can flush without "Done" being clicked
    useEffect(() => {
        if (dubSyncEditLang) {
            setPendingDubSyncEditsRef(dubSyncEditLang, pendingCuesMap[dubSyncEditLang] || []);
        } else {
            setPendingDubSyncEditsRef(null, null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dubSyncEditLang, pendingCuesMap]);

    const enterDubSyncEditMode = (lang, initialCues = null) => {
        const cues = initialCues ?? subtitlesByLanguage[`${lang}-DubSync`] ?? [];
        setPendingCuesMap((prev) => ({ ...prev, [lang]: cues }));
        setPendingCuesHistory([]);  // clear DubSync undo history when entering edit mode
        setSelectedDubSyncCueId(null);
        setDubSyncEditLang(lang);
    };

    const exitDubSyncEditMode = () => {
        setPendingDubSyncEditsRef(null, null);
        setDubSyncEditLang(null);
        setSelectedDubSyncCueId(null);
    };

    const saveDubSyncEdits = (lang) => {
        const newCues = pendingCuesMap[lang];
        if (newCues) {
            updateDubSyncCues(lang, newCues);
            // Pass newCues directly so saveTimeline doesn't read from stale state
            // (React won't have re-rendered yet when this line executes).
            saveDubTimeline(lang, newCues);
        }
        exitDubSyncEditMode();
    };

    // cursorPos: character offset inside the cue text (selectionStart from textarea).
    // When provided, the text is split there and timing is proportional.
    // When null, falls back to splitting at the playhead (or cue midpoint).
    const splitSelectedDubSyncCue = (lang, cursorPos = null) => {
        const cues = pendingCuesMap[lang] || [];
        const cue = cues.find((c) => c.id === selectedDubSyncCueId);
        if (!cue) return;
        savePendingCueSnapshot();

        const text = cue.text || '';
        let splitAt, text1, text2;

        if (cursorPos !== null && cursorPos > 0 && cursorPos < text.length) {
            // ── Cursor-based split ──────────────────────────────────────────
            // Snap to the nearest word boundary so we don't cut mid-word.
            // Walk left/right from cursorPos to find the closest space.
            const leftSpace  = text.lastIndexOf(' ', cursorPos - 1);
            const rightSpace = text.indexOf(' ', cursorPos);
            let snapPos = cursorPos;
            if (leftSpace === -1 && rightSpace === -1) {
                snapPos = cursorPos; // only one word — split in the middle of it
            } else if (leftSpace === -1) {
                snapPos = rightSpace;
            } else if (rightSpace === -1) {
                snapPos = leftSpace;
            } else {
                snapPos = (cursorPos - leftSpace) <= (rightSpace - cursorPos) ? leftSpace : rightSpace;
            }
            text1 = text.slice(0, snapPos).trim();
            text2 = text.slice(snapPos).trim();
            // Timing: proportional to character offset (snapped position)
            const proportion = snapPos / text.length;
            splitAt = cue.startTime + (cue.endTime - cue.startTime) * proportion;
        } else {
            // ── Playhead / midpoint fallback ────────────────────────────────
            const videoTime = playheadTime - (videoStartOffset || 0);
            splitAt = (videoTime > cue.startTime && videoTime < cue.endTime)
                ? videoTime
                : (cue.startTime + cue.endTime) / 2;
            const words = text.split(/\s+/).filter(Boolean);
            const proportion = (splitAt - cue.startTime) / (cue.endTime - cue.startTime);
            const wordCount1 = Math.max(1, Math.min(words.length - 1, Math.round(words.length * proportion)));
            text1 = words.slice(0, wordCount1).join(' ');
            text2 = words.slice(wordCount1).join(' ');
        }

        const newId = `${cue.id}_s${Date.now()}`;
        const newCues = cues.flatMap((c) =>
            c.id !== selectedDubSyncCueId
                ? [c]
                : [
                      { ...c, endTime: splitAt, text: text1 },
                      { id: newId, startTime: splitAt, endTime: c.endTime, text: text2 },
                  ],
        );
        setPendingCuesMap((prev) => ({ ...prev, [lang]: newCues }));
        setSelectedDubSyncCueId(null);
        setSplitCursorPos(null);
    };

    const deleteSelectedDubSyncCue = (lang) => {
        savePendingCueSnapshot();
        const cues = pendingCuesMap[lang] || [];
        setPendingCuesMap((prev) => ({
            ...prev,
            [lang]: cues.filter((c) => c.id !== selectedDubSyncCueId),
        }));
        setSelectedDubSyncCueId(null);
    };

    const runAutoSplitOnPending = (lang) => {
        savePendingCueSnapshot();
        const cues = pendingCuesMap[lang] || [];
        // Primary: same-language subtitle track. Fallback: first non-DubSync track available.
        const origLang = lang.replace(/-DubSync$/, '');
        const origCues =
            subtitlesByLanguage[origLang]?.length > 0
                ? subtitlesByLanguage[origLang]
                : subtitlesByLanguage[lang]?.length > 0
                    ? subtitlesByLanguage[lang]
                    : Object.entries(subtitlesByLanguage)
                          .filter(([k]) => !k.endsWith('-DubSync') && !k.endsWith('-Whisper'))
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([, v]) => v)
                          .find((v) => v?.length > 0) || [];

        // ── DEBUG: copy these three blocks from the console to share for analysis ──
        console.group(`[AutoSplit DEBUG] lang=${lang}`);
        console.log('1) ORIGINAL cues (' + origCues.length + ') — key: startTime, endTime, text');
        console.table(origCues.map((c, i) => ({
            i,
            start: c.startTime?.toFixed(3),
            end:   c.endTime?.toFixed(3),
            words: (c.text || '').split(/\s+/).filter(Boolean).length,
            text:  (c.text || '').slice(0, 60),
        })));
        console.log('2) DUBSYNC INPUT cues (' + cues.length + ') — these are the aligned cues before split');
        console.table(cues.map((c, i) => ({
            i,
            id:    c.id,
            start: c.startTime?.toFixed(3),
            end:   c.endTime?.toFixed(3),
            words: (c.text || '').split(/\s+/).filter(Boolean).length,
            text:  (c.text || '').slice(0, 60),
        })));
        // ── END DEBUG ──

        const split = autoSplitCues(cues, origCues);

        // ── DEBUG: result ──
        console.log('3) SPLIT OUTPUT cues (' + split.length + ')');
        console.table(split.map((c, i) => ({
            i,
            start: c.startTime?.toFixed(3),
            end:   c.endTime?.toFixed(3),
            words: (c.text || '').split(/\s+/).filter(Boolean).length,
            text:  (c.text || '').slice(0, 60),
        })));
        console.log('RAW JSON (copy for analysis):');
        console.log(JSON.stringify({
            originalCues: origCues.map((c, i) => ({ i, startTime: c.startTime, endTime: c.endTime, text: c.text })),
            dubSyncInputCues: cues.map((c, i) => ({ i, id: c.id, startTime: c.startTime, endTime: c.endTime, text: c.text })),
            splitOutputCues: split.map((c, i) => ({ i, id: c.id, startTime: c.startTime, endTime: c.endTime, text: c.text })),
        }, null, 2));
        console.groupEnd();
        // ── END DEBUG ──

        setPendingCuesMap((prev) => ({ ...prev, [lang]: split }));
        setSelectedDubSyncCueId(null);
    };


    const leftOffset = (videoStartOffset || 0) * zoomLevel; // px shift for video/subtitle/audio tracks

    const tracksRef = useRef(null);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [visibleWidth, setVisibleWidth] = useState(800);
    const [showDeleted, setShowDeleted] = useState(false);
    const rulerRef = useRef(null);

    // Attach scrub handlers so user can drag the playhead (pointer events)
    const { onPointerDown, onPointerMove, onPointerUp } = useTimelineScrub({
        containerRef: rulerRef,
        onSeek: (t) => setPlayhead(t),
        zoomLevel,
        scrollLeft,
        leftOffset: 90, // account for label column width
    });

    // Right-click context menu for track labels
    const [trackMenu, setTrackMenu] = useState(null); // { x, y, type: 'video'|'audio', trackId? }
    const openTrackMenu = (e, type, trackId = null) => {
        e.preventDefault();
        setTrackMenu({ x: e.clientX, y: e.clientY, type, trackId });
    };
    const closeTrackMenu = () => setTrackMenu(null);

    // Track which tracks operations cascade to
    const [affectedTracks, setAffectedTracks] = useState({});

    // Sync affectedTracks when languages / audioTracks / logoTracks change
    useEffect(() => {
        setAffectedTracks((prev) => {
            const next = {};
            // Language tracks — default ON
            Object.keys(subtitlesByLanguage).forEach((lang) => {
                next[lang] = prev[lang] !== undefined ? prev[lang] : true;
            });
            // Audio tracks — default OFF
            audioTracks.forEach((t) => {
                const key = `audio_${t.id}`;
                next[key] = prev[key] !== undefined ? prev[key] : false;
            });
            // Logo tracks — default OFF
            if (logoTracks.length > 0) next['Logo'] = prev['Logo'] !== undefined ? prev['Logo'] : false;
            return next;
        });
    }, [subtitlesByLanguage, audioTracks, logoTracks]);

    const toggleTrack = (key) =>
        setAffectedTracks((prev) => ({ ...prev, [key]: !prev[key] }));

    const getAffectedLanguages = useCallback(
        () => Object.keys(affectedTracks).filter((k) => !k.startsWith('audio_') && k !== 'Logo' && affectedTracks[k]),
        [affectedTracks],
    );
    const getAffectMusic = useCallback(
        () => audioTracks.some((t) => affectedTracks[`audio_${t.id}`] ?? false),
        [affectedTracks, audioTracks],
    );
    const getAffectLogo = useCallback(
        () => affectedTracks['Logo'] ?? false,
        [affectedTracks],
    );

    // Track visible area
    useEffect(() => {
        const el = tracksRef.current;
        if (!el) return;
        const handler = () => {
            setScrollLeft(el.scrollLeft);
            setVisibleWidth(el.clientWidth);
        };
        el.addEventListener('scroll', handler);
        const ro = new ResizeObserver(handler);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', handler);
            ro.disconnect();
        };
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const onKeyDown = (e) => {
            // Don't fire if user is typing in an input
            if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;

            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (selectedSegment) {
                    rippleDeleteSegment(
                        selectedSegment.startTime,
                        selectedSegment.endTime,
                        getAffectedLanguages(),
                        getAffectMusic(),
                        getAffectLogo(),
                    );
                }
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                e.preventDefault();
                if (dubSyncEditLang && pendingCuesHistory.length > 0) {
                    // Undo within DubSync inline editor (local history, not reducer)
                    const prev = pendingCuesHistory[pendingCuesHistory.length - 1];
                    setPendingCuesMap(prev);
                    setPendingCuesHistory((h) => h.slice(0, -1));
                    setSelectedDubSyncCueId(null);
                } else if (e.shiftKey) {
                    redo();
                } else {
                    undo();
                }
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
                e.preventDefault();
                redo();
            }
            if (e.key === 'v') setTool(Tools.SELECT);
            // Removed 'c' razor shortcut — splitting is handled via right-click menu
            if (e.key === 'h') setTool(Tools.HAND);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selectedSegment, rippleDeleteSegment, undo, redo, setTool, dubSyncEditLang, pendingCuesHistory, setPendingCuesMap, setPendingCuesHistory, getAffectedLanguages, getAffectMusic, getAffectLogo]);

    const outputDuration = getOutputDuration(duration, cuts);
    const totalWidth = ((showDeleted ? duration : outputDuration) + (videoStartOffset || 0) + (videoEndOffset || 0)) * zoomLevel;

    // Click on ruler → seek
    const handleRulerSeek = useCallback(
        (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left - 90 + scrollLeft;
            const originalTime = Math.max(0, x / zoomLevel);
            setPlayhead(originalTime);
        },
        [scrollLeft, zoomLevel, setPlayhead],
    );

    const handleRazorClick = useCallback(
        (time) => {
            addCutMarkerWithSplit(parseFloat(time.toFixed(3)), getAffectedLanguages());
        },
        [addCutMarkerWithSplit, getAffectedLanguages],
    );

    const handleSegmentClick = useCallback(
        (seg) => {
            if (
                selectedSegment &&
                Math.abs(selectedSegment.startTime - seg.startTime) < 0.001
            ) {
                selectSegment(null); // deselect
            } else {
                selectSegment(seg);
            }
        },
        [selectedSegment, selectSegment],
    );

    // Scroll playhead into view — trigger when playhead passes 80% of the visible content area
    useEffect(() => {
        const el = tracksRef.current;
        if (!el) return;
        const x = playheadTime * zoomLevel;        // absolute pixel in TracksContent
        const labelW = 90;                          // frozen label column width
        const contentW = el.clientWidth - labelW;   // visible scrollable content width
        const relX = x - el.scrollLeft - labelW;    // playhead pos relative to content viewport
        if (relX > contentW * 0.8 || relX < 0) {
            // Land the playhead at ~20% from the left edge of the content viewport
            el.scrollLeft = Math.max(0, x - labelW - contentW * 0.2);
        }
    }, [playheadTime, zoomLevel]);

    const langKeys = Object.keys(subtitlesByLanguage);
    const visibleLangKeys = langKeys.filter((l) => !hiddenTimelineLangs.has(l));

    return (
        <PanelRoot>
            {/* Toolbar */}
            <Toolbar>
                <Tooltip title="Select (V)">
                    <ToolBtn
                        size="small"
                        active={activeTool === Tools.SELECT ? 'true' : 'false'}
                        onClick={() => setTool(Tools.SELECT)}
                    >
                        <TouchAppIcon sx={{ fontSize: 16 }} />
                    </ToolBtn>
                </Tooltip>

                {/* Razor/Cut tool removed from toolbar — use right-click Split at Playhead */}

                <Tooltip title="Hand / Scroll (H)">
                    <ToolBtn
                        size="small"
                        active={activeTool === Tools.HAND ? 'true' : 'false'}
                        onClick={() => setTool(Tools.HAND)}
                    >
                        <PanToolIcon sx={{ fontSize: 16 }} />
                    </ToolBtn>
                </Tooltip>

                <Divider />

                <Tooltip title="Delete selected segment (Del)">
                    <span>
                        <ToolBtn
                            size="small"
                            active="false"
                            disabled={!selectedSegment}
                            onClick={() =>
                                selectedSegment &&
                                rippleDeleteSegment(
                                    selectedSegment.startTime,
                                    selectedSegment.endTime,
                                    getAffectedLanguages(),
                                    getAffectMusic(),
                                    getAffectLogo(),
                                )
                            }
                        >
                            <DeleteIcon sx={{ fontSize: 16, color: selectedSegment ? '#ef5350' : undefined }} />
                        </ToolBtn>
                    </span>
                </Tooltip>

                <Divider />

                <Tooltip title="Undo (⌘Z)">
                    <span>
                        <ToolBtn
                            size="small"
                            active="false"
                            disabled={state.past.length === 0}
                            onClick={undo}
                        >
                            <UndoIcon sx={{ fontSize: 16 }} />
                        </ToolBtn>
                    </span>
                </Tooltip>

                <Tooltip title="Redo (⌘⇧Z)">
                    <span>
                        <ToolBtn
                            size="small"
                            active="false"
                            disabled={state.future.length === 0}
                            onClick={redo}
                        >
                            <RedoIcon sx={{ fontSize: 16 }} />
                        </ToolBtn>
                    </span>
                </Tooltip>

                <Divider />

                <Tooltip title="Zoom Out">
                    <ToolBtn size="small" active="false" onClick={() => setZoom(zoomLevel * 0.7)}>
                        <ZoomOutIcon sx={{ fontSize: 16 }} />
                    </ToolBtn>
                </Tooltip>

                <Typography variant="caption" sx={{ color: '#666', fontSize: 12, mx: 0.5 }}>
                    {zoomLevel.toFixed(0)}px/s
                </Typography>

                <Tooltip title="Zoom In">
                    <ToolBtn size="small" active="false" onClick={() => setZoom(zoomLevel * 1.4)}>
                        <ZoomInIcon sx={{ fontSize: 16 }} />
                    </ToolBtn>
                </Tooltip>

                {/* Tool hint — ? button, right-aligned */}
                {(TOOL_HINTS[activeTool] || []).length > 0 && (
                    <Tooltip
                        title={
                            <Box sx={{ p: 0.5 }}>
                                {(TOOL_HINTS[activeTool] || []).map((hint, i) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: i < (TOOL_HINTS[activeTool] || []).length - 1 ? 0.5 : 0 }}>
                                        {hint.step && (
                                            <Box component="span" sx={{ background: 'rgba(100,181,246,0.15)', border: '1px solid rgba(100,181,246,0.35)', borderRadius: '3px', px: 0.6, fontSize: 11, color: '#90caf9', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                {hint.step}
                                            </Box>
                                        )}
                                        <span style={{ fontSize: 12 }}>{hint.text}</span>
                                    </Box>
                                ))}
                            </Box>
                        }
                        placement="bottom-end"
                        arrow
                    >
                        <ToolBtn size="small" active="false" sx={{ color: '#555', ml: 'auto' }}>
                            <HelpOutlineIcon sx={{ fontSize: 15 }} />
                        </ToolBtn>
                    </Tooltip>
                )}

                {selectedSegment && (
                    <Chip
                        label={`Selected: ${secondsToTimecode(selectedSegment.startTime, 25).slice(0, 8)} → ${secondsToTimecode(selectedSegment.endTime, 25).slice(0, 8)}`}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ fontSize: 12 }}
                        onDelete={() => selectSegment(null)}
                    />
                )}

                {cuts.length > 0 && (
                    <Tooltip title={showDeleted ? 'Hide deleted segments' : 'Show deleted segments'}>
                        <ToolBtn
                            size="small"
                            active={showDeleted ? 'true' : 'false'}
                            onClick={() => setShowDeleted((v) => !v)}
                            sx={{ color: showDeleted ? '#ef9a9a' : '#555' }}
                        >
                            {showDeleted ? <VisibilityIcon sx={{ fontSize: 16 }} /> : <VisibilityOffIcon sx={{ fontSize: 16 }} />}
                        </ToolBtn>
                    </Tooltip>
                )}

                {cuts.length > 0 && (
                    <Typography variant="caption" sx={{ color: '#555', fontSize: 12, ml: 1 }}>
                        {cuts.length} cut{cuts.length !== 1 ? 's' : ''}
                    </Typography>
                )}
            </Toolbar>

            {/* Time Ruler */}
            <RulerRow
                ref={rulerRef}
                onClick={handleRulerSeek}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                style={{ cursor: 'pointer', touchAction: 'none' }}
            >
                <TimeRuler
                    duration={duration}
                    zoomLevel={zoomLevel}
                    scrollLeft={scrollLeft}
                    visibleWidth={visibleWidth}
                    labelWidth={90}
                />
                {/* Playhead needle on ruler */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: `${90 + playheadTime * zoomLevel - scrollLeft}px`,
                        width: '2px',
                        height: '100%',
                        background: '#e53935',
                        pointerEvents: 'none',
                        zIndex: 15,
                    }}
                />
            </RulerRow>

            {/* Tracks */}
            <TracksArea ref={tracksRef}>
                <TracksContent style={{ width: `${totalWidth + 90}px` }}>

                    {/* Video Track */}
                    <TrackRow>
                        <TrackLabel
                            onContextMenu={(e) => openTrackMenu(e, 'video')}
                            title="Right-click for options"
                            style={muteVideoAudio ? { color: '#ef9a9a', textDecoration: 'line-through' } : {}}
                        >🎬 Video</TrackLabel>
                        <TrackContent>
                            <VideoTrack
                                duration={duration}
                                cutMarkers={cutMarkers}
                                cuts={cuts}
                                zoomLevel={zoomLevel}
                                scrollLeft={scrollLeft}
                                activeTool={activeTool}
                                selectedSegment={selectedSegment}
                                playheadTime={playheadTime}
                                videoTitle={state.videoTitle}
                                onSegmentClick={handleSegmentClick}
                                onRazorClick={handleRazorClick}
                                onRemoveMarker={removeCutMarker}
                                showDeleted={showDeleted}
                                leftOffset={leftOffset}
                                onDeleteSegment={(seg) =>
                                    rippleDeleteSegment(
                                        seg.startTime,
                                        seg.endTime,
                                        getAffectedLanguages(),
                                        getAffectMusic(),
                                        getAffectLogo(),
                                    )
                                }
                            />
                        </TrackContent>
                    </TrackRow>

                    {/* Subtitle Tracks — one per visible language */}
                    {visibleLangKeys.map((lang) => {
                        // DubSync subtitle tracks are rendered separately below via DubSyncTrackBar
                        if (lang.endsWith('-DubSync')) return null;
                        // Raw Whisper tracks are rendered separately below as read-only reference rows
                        if (lang.endsWith('-Whisper')) return null;
                        const subtitleLabel = `💬 ${lang.slice(0, 8)}`;
                        return (
                        <TrackRow key={lang}>
                            <TrackLabel title={lang}>{subtitleLabel}</TrackLabel>
                            <TrackContent>
                                <SubtitleTrackBar
                                    cues={subtitlesByLanguage[lang]}
                                    zoomLevel={zoomLevel}
                                    scrollLeft={scrollLeft}
                                    language={lang}
                                    outputDuration={outputDuration}
                                    cuts={cuts}
                                    showDeleted={showDeleted}
                                    leftOffset={leftOffset}
                                    onCueDrop={(cueId, deltaSeconds, ripple) =>
                                        moveSubtitleCues(lang, cueId, deltaSeconds, ripple)
                                    }
                                />
                            </TrackContent>
                        </TrackRow>
                        );
                    })}

                    {/* Audio Tracks */}
                    {audioTracks.map((track) => (
                        <TrackRow key={track.id}>
                            <TrackLabel
                                onContextMenu={(e) => openTrackMenu(e, 'audio', track.id)}
                                title={`${track.name} — right-click for options`}
                                style={track.muted ? { color: '#ef9a9a', textDecoration: 'line-through' } : {}}
                            >🎵 {track.name.slice(0, 8)}</TrackLabel>
                            <TrackContent>
                                <MusicTrackBar
                                    duration={duration}
                                    track={track}
                                    zoomLevel={zoomLevel}
                                    scrollLeft={scrollLeft}
                                    leftOffset={leftOffset}
                                />
                            </TrackContent>
                        </TrackRow>
                    ))}

                    {/* Logo Overlay Tracks */}
                    {logoTracks.map((lt) => (
                        <TrackRow key={lt.id}>
                            <TrackLabel title={lt.labelRole || lt.name}>🖼 {(lt.labelRole || lt.name || '').slice(0, 8)}</TrackLabel>
                            <TrackContent>
                                <LogoTrackBar
                                    track={lt}
                                    duration={duration}
                                    zoomLevel={zoomLevel}
                                    onUpdate={(patch) => updateLogoTrack(lt.id, patch)}
                                />
                            </TrackContent>
                        </TrackRow>
                    ))}

                    {/* DubSync Tracks — one per language where DubSync has been enabled */}
                    {(dubSyncTracks || []).map((dsTrack) => {
                        const dubSyncLangKey = `${dsTrack.language}-DubSync`;
                        const cues = subtitlesByLanguage[dubSyncLangKey] || [];
                        const isBusy = dsTrack.trackState === 'SYNCING' || dsTrack.trackState === 'RENDERING';
                        const statusEmoji = {
                            NO_AUDIO:  '📂',
                            READY:     '✅',
                            SYNCING:   '⏳',
                            SYNCED:    '🎯',
                            RENDERING: '⏳',
                            RENDERED:  '🎵',
                            FAILED:    '❌',
                        }[dsTrack.trackState] || '🎙';
                        const labelText = `${statusEmoji} ${dsTrack.language.slice(0, 4)} sync`;
                        return (
                            <TrackRow key={dsTrack.language}>
                                <TrackLabel
                                    onContextMenu={(e) => openTrackMenu(e, 'dubsync', dsTrack.language)}
                                    title={`${dsTrack.language} DubSync — right-click for options${dsTrack.error ? `\n⚠ ${dsTrack.error}` : ''}`}
                                    style={dsTrack.trackState === 'FAILED' ? { color: '#ef9a9a' } : isBusy ? { color: '#ffe082' } : {}}
                                >
                                    {labelText}
                                </TrackLabel>
                                <TrackContent>
                                <DubSyncTrackBar
                                        cues={dubSyncEditLang === dsTrack.language
                                            ? (pendingCuesMap[dsTrack.language] || cues)
                                            : cues}
                                        problematicCueIndices={dubSyncEditLang === dsTrack.language ? [] : (dsTrack.problematicCues || [])}
                                        zoomLevel={zoomLevel}
                                        scrollLeft={scrollLeft}
                                        leftOffset={leftOffset}
                                        editMode={dubSyncEditLang === dsTrack.language}
                                        selectedCueId={selectedDubSyncCueId}
                                        onCueSelect={(id) => setSelectedDubSyncCueId(id)}
                                        onCueChange={(newCues) => setPendingCuesMap((prev) => ({ ...prev, [dsTrack.language]: newCues }))}
                                        onDragStart={savePendingCueSnapshot}
                                        onCueSplit={dubSyncEditLang === dsTrack.language
                                            ? (cueId, splitAtTime) => splitPendingCue(dsTrack.language, cueId, splitAtTime)
                                            : undefined}
                                    />
                                </TrackContent>
                            </TrackRow>
                        );
                    })}

                    {/* Whisper Reference Tracks — raw Whisper transcription with its own timestamps */}
                    {visibleLangKeys
                        .filter((k) => k.endsWith('-Whisper'))
                        .map((whisperKey) => {
                            const baseLang = whisperKey.slice(0, -('-Whisper'.length));
                            return (
                                <TrackRow key={whisperKey}>
                                    <TrackLabel title={`${baseLang} — raw Whisper transcription (reference, read-only)`}>
                                        🔤 {baseLang.slice(0, 4)} raw
                                    </TrackLabel>
                                    <TrackContent>
                                        <SubtitleTrackBar
                                            cues={subtitlesByLanguage[whisperKey]}
                                            zoomLevel={zoomLevel}
                                            scrollLeft={scrollLeft}
                                            language={whisperKey}
                                            outputDuration={outputDuration}
                                            cuts={cuts}
                                            showDeleted={showDeleted}
                                            leftOffset={leftOffset}
                                        />
                                    </TrackContent>
                                </TrackRow>
                            );
                        })}

                    {/* Playhead overlay across all tracks */}
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: `${90 + playheadTime * zoomLevel}px`,
                            width: '2px',
                            height: '100%',
                            background: 'rgba(229,57,53,0.7)',
                            pointerEvents: 'none',
                            zIndex: 20,
                        }}
                    />
                </TracksContent>
            </TracksArea>

            {/* Delete hint */}
            {selectedSegment && (
                <DeleteHint>
                    Press ⌫ Delete to ripple-delete · tracks affected per ‘Apply to:’ row above
                </DeleteHint>
            )}
            {/* Track label right-click context menu */}
            <Menu
                open={Boolean(trackMenu)}
                onClose={closeTrackMenu}
                anchorReference="anchorPosition"
                anchorPosition={trackMenu ? { top: trackMenu.y, left: trackMenu.x } : undefined}
                PaperProps={{
                    sx: {
                        background: '#1a1a2e',
                        border: '1px solid #2a2a4a',
                        minWidth: 180,
                        color: '#e0e0ff',
                        '& .MuiMenuItem-root': { color: '#e0e0ff' },
                        '& .MuiMenuItem-root:hover': { background: 'rgba(100,181,246,0.12)' },
                        '& .MuiListItemIcon-root': { color: '#e0e0ff' },
                    },
                }}
            >
                {trackMenu?.type === 'video' && [
                    <MenuItem key="video-volume" sx={{ fontSize: 13, color: '#e0e0ff' }}>
                        <Box sx={{ width: 260, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ minWidth: 48 }}>Volume</Typography>
                            <Slider
                                size="small"
                                min={0}
                                max={1}
                                step={0.01}
                                value={(state && typeof state.videoVolume === 'number') ? state.videoVolume : 1}
                                onChange={(_, v) => setVideoVolume(v)}
                                sx={{ flex: 1 }}
                            />
                            <Typography variant="caption">{Math.round((state.videoVolume || 1) * 100)}%</Typography>
                        </Box>
                    </MenuItem>,
                    <MenuItem
                        key="mute-video"
                        onClick={() => { toggleMuteVideo(); closeTrackMenu(); }}
                        sx={{ fontSize: 13, color: '#e0e0ff' }}
                    >
                        <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>
                            {muteVideoAudio ? '🔊' : '🔇'}
                        </ListItemIcon>
                        {muteVideoAudio ? 'Unmute video audio' : 'Mute video audio'}
                    </MenuItem>,
                ]}
                {trackMenu?.type === 'audio' && (() => {
                    const t = audioTracks.find((a) => a.id === trackMenu.trackId);
                    if (!t) return null;
                    return [
                        <MenuItem key="audio-volume" sx={{ fontSize: 13, color: '#e0e0ff' }}>
                            <Box sx={{ width: 260, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" sx={{ minWidth: 44 }}>Volume</Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    value={typeof t.volume === 'number' ? t.volume : 1}
                                    onChange={(_, v) => updateAudioTrack(t.id, { volume: v })}
                                    sx={{ flex: 1 }}
                                />
                                <Typography variant="caption">{Math.round((t.volume || 1) * 100)}%</Typography>
                            </Box>
                        </MenuItem>,
                        <MenuItem
                            key="mute-track"
                            onClick={() => { updateAudioTrack(t.id, { muted: !t.muted }); closeTrackMenu(); }}
                            sx={{ fontSize: 13, color: '#e0e0ff' }}
                        >
                            <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>
                                {t.muted ? '🔊' : '🔇'}
                            </ListItemIcon>
                            {t.muted ? 'Unmute track' : 'Mute track'}
                        </MenuItem>,
                        <MenuItem
                            key="remove-track"
                            onClick={() => { updateAudioTrack(t.id, { volume: 0 }); closeTrackMenu(); }}
                            sx={{ fontSize: 13, color: '#9090b0' }}
                        >
                            <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>🔈</ListItemIcon>
                            Set volume to 0
                        </MenuItem>,
                    ];
                })()}
                {trackMenu?.type === 'dubsync' && (() => {
                    const lang = trackMenu.trackId; // trackId holds the language string for DubSync
                    const dsTrack = (dubSyncTracks || []).find((t) => t.language === lang);
                    if (!dsTrack) return null;
                    const canSync   = ['READY', 'SYNCED', 'RENDERED', 'FAILED'].includes(dsTrack.trackState);
                    const canRender = dsTrack.trackState === 'SYNCED';
                    const isBusy    = dsTrack.trackState === 'SYNCING' || dsTrack.trackState === 'RENDERING';
                    const mode      = dubSyncModeMap[lang] || 'ai';
                    const setMode   = (m) => setDubSyncModeMap((prev) => ({ ...prev, [lang]: m }));
                    const aiOptionsOpen = showAiOptionsMap[lang] || false;
                    const toggleAiOptions = (e) => { e.stopPropagation(); setShowAiOptionsMap((prev) => ({ ...prev, [lang]: !prev[lang] })); };
                    const hasSynced = dsTrack.trackState === 'SYNCED' || dsTrack.trackState === 'RENDERED';
                    return [
                        /* ── Header ── */
                        <MenuItem key="ds-label" disabled sx={{ fontSize: 11, color: '#6060a0', opacity: 0.9 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <span>🎙 {lang} DubSync — {dsTrack.trackState}</span>
                                {dsTrack.error && (
                                    <Tooltip title={dsTrack.error} placement="right" arrow>
                                        <span style={{ cursor: 'default', color: '#ef9a9a', fontWeight: 700 }}>⚠</span>
                                    </Tooltip>
                                )}
                            </Box>
                        </MenuItem>,

                        /* ── AI / Manual toggle ── */
                        <MenuItem key="ds-mode-toggle" disableRipple sx={{ fontSize: 12, py: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography variant="caption" sx={{ color: mode === 'ai' ? '#ffe082' : '#9090b0', minWidth: 24 }}>AI</Typography>
                                <Switch
                                    size="small"
                                    checked={mode === 'manual'}
                                    onChange={async (e) => {
                                        const toManual = e.target.checked;
                                        setMode(toManual ? 'manual' : 'ai');
                                        if (toManual && !isBusy) {
                                            closeTrackMenu();
                                            const existingCues = subtitlesByLanguage[`${lang}-DubSync`] || [];
                                            if (!hasSynced && existingCues.length === 0) {
                                                // First time ever — copy base-language VTT as reference, then open editor
                                                const cues = await bootstrapManual(lang);
                                                if (cues) enterDubSyncEditMode(lang, cues);
                                            } else {
                                                // Already has cues (SYNCED, RENDERED, or restored from a previous session)
                                                enterDubSyncEditMode(lang);
                                            }
                                        }
                                    }}
                                    sx={{ mx: 0.5 }}
                                />
                                <Typography variant="caption" sx={{ color: mode === 'manual' ? '#c8e6c9' : '#9090b0' }}>Manual</Typography>
                            </Box>
                        </MenuItem>,

                        /* ── AI mode items ── */
                        mode === 'ai' && (
                            <MenuItem
                                key="sync-magic"
                                disabled={!canSync || isBusy}
                                onClick={() => {
                                    try { console.info('DubSync: user triggered Sync Magic', { videoId: state.videoId, language: lang, forceTranscribe: dubSyncForceTranscribe, noCheapMode: dubSyncNoCheapMode, withSrtHint: dubSyncWithSrtHint }); } catch(_){}
                                    syncMagic(lang, dsTrack.uploadedAudioFile, { forceTranscribe: dubSyncForceTranscribe, noCheapMode: dubSyncNoCheapMode, withSrtHint: dubSyncWithSrtHint });
                                    closeTrackMenu();
                                }}
                                sx={{ fontSize: 13, color: canSync && !isBusy ? '#ffe082' : '#555' }}
                            >
                                <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>✨</ListItemIcon>
                                {isBusy ? 'Working…' : 'Sync Magic'}
                                <Box sx={{ flex: 1 }} />
                                <Tooltip title={aiOptionsOpen ? 'Hide AI options' : 'AI options'} placement="right">
                                    <span
                                        role="button"
                                        aria-label="toggle AI options"
                                        style={{ fontSize: 14, opacity: 0.7, cursor: 'pointer', padding: '0 4px' }}
                                        onClick={toggleAiOptions}
                                    >⚙</span>
                                </Tooltip>
                            </MenuItem>
                        ),
                        mode === 'ai' && aiOptionsOpen && (
                            <MenuItem key="force-transcribe" onClick={() => setDubSyncForceTranscribe((v) => !v)} sx={{ fontSize: 12, color: dubSyncForceTranscribe ? '#ffcc80' : '#9090b0', pl: 4 }}>
                                <ListItemIcon sx={{ minWidth: 30, fontSize: 14 }}>{dubSyncForceTranscribe ? '☑️' : '☐'}</ListItemIcon>
                                Force Re-transcribe
                            </MenuItem>
                        ),
                        mode === 'ai' && aiOptionsOpen && (
                            <MenuItem key="no-cheap-mode" onClick={() => setDubSyncNoCheapMode((v) => !v)} sx={{ fontSize: 12, color: dubSyncNoCheapMode ? '#ffcc80' : '#9090b0', pl: 4 }}>
                                <ListItemIcon sx={{ minWidth: 30, fontSize: 14 }}>{dubSyncNoCheapMode ? '☑️' : '☐'}</ListItemIcon>
                                Disable Cheap Mode
                            </MenuItem>
                        ),
                        mode === 'ai' && aiOptionsOpen && (
                            <MenuItem key="with-srt-hint" onClick={() => setDubSyncWithSrtHint((v) => !v)} sx={{ fontSize: 12, color: dubSyncWithSrtHint ? '#ffcc80' : '#9090b0', pl: 4 }}>
                                <ListItemIcon sx={{ minWidth: 30, fontSize: 14 }}>{dubSyncWithSrtHint ? '☑️' : '☐'}</ListItemIcon>
                                Pass SRT hint to AI
                            </MenuItem>
                        ),
                        hasSynced && (
                            <MenuItem
                                key="edit-cues"
                                onClick={() => { enterDubSyncEditMode(lang); closeTrackMenu(); }}
                                sx={{ fontSize: 13, color: '#b0d0ff' }}
                            >
                                <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>✏️</ListItemIcon>
                                Edit Cues
                            </MenuItem>
                        ),

                        /* ── Manual mode items ── */
                        mode === 'manual' && hasSynced && (
                            <Tooltip
                                key="reset-bootstrap-tooltip"
                                title={`Overwrite current timings by re-copying from the base ${lang} subtitles. This cannot be undone.`}
                                placement="right"
                                arrow
                            >
                                <MenuItem
                                    key="reset-bootstrap"
                                    disabled={isBusy}
                                    onClick={async () => {
                                        closeTrackMenu();
                                        const cues = await bootstrapManual(lang);
                                        if (cues) enterDubSyncEditMode(lang, cues);
                                    }}
                                    sx={{ fontSize: 12, color: '#6060a0', opacity: 0.7 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 30, fontSize: 14 }}>↺</ListItemIcon>
                                    Reset from {lang}…
                                </MenuItem>
                            </Tooltip>
                        ),

                        /* ── Always-visible items ── */
                        <MenuItem
                            key="render-audio"
                            disabled={!canRender}
                            onClick={() => { try { console.info('DubSync: user triggered Render Synced Audio', { videoId: state.videoId, language: lang }); } catch(_){}; renderDubAudio(lang); closeTrackMenu(); }}
                            sx={{ fontSize: 13, color: canRender ? '#e0e0ff' : '#555' }}
                        >
                            <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>🎵</ListItemIcon>
                            Render Synced Audio
                        </MenuItem>,
                        <MenuItem
                            key="remove-dubsync"
                            onClick={() => {
                                try { console.info('DubSync: user removed track', { videoId: state.videoId, language: lang }); } catch(_){}
                                removeDubSyncTrackFn(lang);
                                closeTrackMenu();
                            }}
                            sx={{ fontSize: 13, color: '#9090b0' }}
                        >
                            <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>🗑</ListItemIcon>
                            Remove DubSync Track
                        </MenuItem>,
                    ].filter(Boolean);
                })()}
            </Menu>

            {/* ── DubSync inline cue edit strip ── rendered outside the scroll area */}
            {dubSyncEditLang && (() => {
                const editCues = pendingCuesMap[dubSyncEditLang] || [];
                const selCue = editCues.find((c) => c.id === selectedDubSyncCueId) || null;
                const origCues = subtitlesByLanguage[dubSyncEditLang] || [];
                return (
                    <Box
                        sx={{
                            flexShrink: 0,
                            background: '#0e0e20',
                            borderTop: '1px solid #2a2a4a',
                            p: '6px 10px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '5px',
                        }}
                    >
                        {/* Action toolbar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: '#7070a0', mr: 0.5 }}>
                                ✏ <strong style={{ color: '#9090d0' }}>{dubSyncEditLang}</strong> DubSync editing
                                &nbsp;·&nbsp;{editCues.length} cues
                                {selCue && (
                                    <>
                                        &nbsp;·&nbsp;
                                        <span style={{ color: '#9090c0' }}>
                                            {selCue.startTime.toFixed(2)}s → {selCue.endTime.toFixed(2)}s
                                        </span>
                                    </>
                                )}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Tooltip title={splitCursorPos ? 'Split text at cursor — timing split proportionally to cursor position' : 'Split at playhead position (or cue midpoint if playhead is outside)'}>
                                <span>
                                    <Button
                                        size="small"
                                        disabled={!selCue}
                                        onClick={() => splitSelectedDubSyncCue(dubSyncEditLang, splitCursorPos)}
                                        sx={{ fontSize: 11, color: splitCursorPos ? '#64b5f6' : '#b0e0ff', minWidth: 0, py: 0,
                                            fontWeight: splitCursorPos ? 700 : 400 }}
                                    >
                                        {splitCursorPos ? '✂ Split at cursor' : '✂ Split'}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Delete selected cue">
                                <span>
                                    <Button size="small" disabled={!selCue} onClick={() => deleteSelectedDubSyncCue(dubSyncEditLang)}
                                        sx={{ fontSize: 11, color: '#ef9a9a', minWidth: 0, py: 0 }}>
                                        🗑 Delete
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button size="small" onClick={exitDubSyncEditMode}
                                sx={{ fontSize: 11, color: '#666', minWidth: 0, py: 0 }}>
                                ✖ Cancel
                            </Button>
                            <Button size="small" variant="contained" onClick={() => saveDubSyncEdits(dubSyncEditLang)}
                                sx={{ fontSize: 11, minWidth: 0, py: 0, background: 'linear-gradient(135deg,#3a7bd5,#8e44ad)' }}>
                                ✔ Done
                            </Button>
                        </Box>

                        {/* Selected cue text editor */}
                        {selCue && (() => {
                            const cueText = selCue.text || '';
                            // Compute split preview when cursor is placed inside the text
                            const hasCursor = splitCursorPos !== null && splitCursorPos > 0 && splitCursorPos < cueText.length;
                            // Snap preview to nearest word boundary (same logic as splitSelectedDubSyncCue)
                            let previewA = null, previewB = null, previewSplitTime = null;
                            if (hasCursor) {
                                const leftSpace  = cueText.lastIndexOf(' ', splitCursorPos - 1);
                                const rightSpace = cueText.indexOf(' ', splitCursorPos);
                                let snap = splitCursorPos;
                                if (leftSpace !== -1 || rightSpace !== -1) {
                                    if (leftSpace === -1) snap = rightSpace;
                                    else if (rightSpace === -1) snap = leftSpace;
                                    else snap = (splitCursorPos - leftSpace) <= (rightSpace - splitCursorPos) ? leftSpace : rightSpace;
                                }
                                previewA = cueText.slice(0, snap).trim();
                                previewB = cueText.slice(snap).trim();
                                previewSplitTime = selCue.startTime + (selCue.endTime - selCue.startTime) * (snap / cueText.length);
                            }
                            return (
                                <>
                                    <Box
                                        component="textarea"
                                        value={cueText}
                                        onChange={(e) => {
                                            const t = e.target.value;
                                            setSplitCursorPos(null);
                                            setPendingCuesMap((prev) => ({
                                                ...prev,
                                                [dubSyncEditLang]: (prev[dubSyncEditLang] || []).map((c) =>
                                                    c.id === selectedDubSyncCueId ? { ...c, text: t } : c,
                                                ),
                                            }));
                                        }}
                                        onSelect={(e) => setSplitCursorPos(e.target.selectionStart)}
                                        onMouseUp={(e) => setSplitCursorPos(e.target.selectionStart)}
                                        onKeyUp={(e) => setSplitCursorPos(e.target.selectionStart)}
                                        rows={2}
                                        placeholder="Type or position your cursor, then click ✂ Split at cursor"
                                        sx={{
                                            width: '100%',
                                            background: '#12122a',
                                            color: '#e0e0ff',
                                            border: hasCursor ? '1px solid #3a6a9a' : '1px solid #3a3a5a',
                                            borderRadius: 1,
                                            fontSize: 12,
                                            p: '4px 8px',
                                            resize: 'vertical',
                                            outline: 'none',
                                            fontFamily: 'inherit',
                                            transition: 'border-color 0.15s',
                                        }}
                                    />
                                    {/* Live split preview */}
                                    {hasCursor ? (
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: '6px', mt: '3px',
                                            fontSize: 11, lineHeight: 1.4, flexWrap: 'wrap',
                                        }}>
                                            <Box component="span" sx={{
                                                background: 'rgba(56,100,160,0.25)', border: '1px solid #3a6a9a',
                                                borderRadius: '3px', px: '5px', py: '1px', color: '#a0c8ff',
                                                maxWidth: '42%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {previewA || '(empty)'}
                                            </Box>
                                            <Box component="span" sx={{ color: '#64b5f6', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>✂</Box>
                                            <Box component="span" sx={{
                                                background: 'rgba(56,100,160,0.25)', border: '1px solid #3a6a9a',
                                                borderRadius: '3px', px: '5px', py: '1px', color: '#a0c8ff',
                                                maxWidth: '42%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {previewB || '(empty)'}
                                            </Box>
                                            <Box component="span" sx={{ color: '#5060a0', ml: '4px', flexShrink: 0 }}>
                                                @ {previewSplitTime.toFixed(2)}s
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" sx={{ color: '#383858', mt: '2px', display: 'block' }}>
                                            💡 Click inside the text to position your cursor, then click ✂ Split at cursor — or split at playhead without positioning
                                        </Typography>
                                    )}
                                </>
                            );
                        })()}
                        {!selCue && (
                            <Typography variant="caption" sx={{ color: '#404060' }}>
                                Click a cue block in the timeline to select it · Drag the left/right edge to adjust timing · Drag the body to move it
                            </Typography>
                        )}
                    </Box>
                );
            })()}
        </PanelRoot>
    );
}
